import { Divider, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import Typography from "components/basic/typography";
import GoogleRating from "components/basic/googleRating";
import VerifiedBadge from "components/basic/verifiedBadge";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { isSimilar } from "utils/fuzzyMatch";
import { haversineKm } from "utils/geo";
import logger from "utils/logger";
import { ChargerEstimatorData } from "pages/chargerEstimator";
import { useNavigate } from "react-router-dom";
import {
  AUTO_SELECT_CHARGER_SETUPS,
  QuoteNavigationState,
  QuoteServiceSource,
} from "../../quote/quoteTools";

interface ServiceField {
  id: number;
  fieldLabel: string;
  fieldName: string;
  customLabel?: string;
  price: number | null;
}

interface UserSelections {
  wireLength: string;
  selectedAddOn: string;
  selectedChargerModel: string;
  selectedChargerModelId: number;
}

type ChargerModelOption = {
  id: number;
  label: string;
  customLabel: string;
};

const DEFAULT_CHARGER_MODEL: ChargerModelOption = {
  id: 0,
  label: "None",
  customLabel: "None",
};

const usePriceBreakdown = (
  fields: ServiceField[] | undefined,
  selections: UserSelections,
) => {
  return useMemo(() => {
    if (!fields?.length) {
      return { items: [], total: 0 };
    }

    // Fields based on user selections
    const relevantFields = fields.filter((field) => {
      if (field.fieldName === "evseType") {
        return (
          selections.selectedChargerModelId > 0 &&
          field.id === selections.selectedChargerModelId
        );
      }
      if (field.fieldName === "cableLength") {
        return isSimilar(selections.wireLength, field.fieldLabel, 1);
      }
      if (field.fieldName === "addOnServices") {
        return isSimilar(selections.selectedAddOn, field.fieldLabel, 0.9);
      }
      return false;
    });

    const items = relevantFields
      .filter(
        (field): field is ServiceField & { price: number } =>
          typeof field.price === "number",
      )
      .map((field) => ({
        id: field.id,
        label: field.fieldLabel,
        fieldName: field.fieldName,
        price: field.price,
      }));

    const total = items.reduce((sum, item) => sum + item.price, 0);

    return { items, total };
  }, [fields, selections]);
};

export const useGetFieldByName = (
  fields: ServiceField[] | undefined,
  fieldName: string,
) => {
  return useMemo(() => {
    if (!fields?.length) return [];

    return fields
      .filter((field) => field.fieldName === fieldName)
      .map((field) => ({
        id: field.id,
        label: field.fieldLabel,
        customLabel: field.customLabel,
      }));
  }, [fields, fieldName]);
};

function getCheapestChargerModel(
  fields: ServiceField[],
  chargerSetup: string,
  chargerLocation: string,
): ChargerModelOption | null {
  const matching = fields.filter(
    (f) =>
      f.fieldName === "evseType" &&
      f.price !== null &&
      isSimilar(`${chargerSetup} (${chargerLocation})`, f.fieldLabel, 1),
  );
  if (!matching.length) return null;
  const cheapest = matching.reduce((a, b) =>
    (a.price ?? Infinity) < (b.price ?? Infinity) ? a : b,
  );
  return {
    id: cheapest.id,
    label: cheapest.customLabel || cheapest.fieldLabel,
    customLabel: cheapest.customLabel || cheapest.fieldLabel,
  };
}

interface ChargerCardProps {
  service: any;
  chargerConfigData: ChargerEstimatorData;
  /** All recommendations shown on this page, so the quote can page through them. */
  allServices?: QuoteServiceSource[];
  /** This card's position within `allServices`. */
  serviceIndex?: number;
  /** Off when the page supplies its own Expand, e.g. the mobile sticky footer. */
  showExpandAction?: boolean;
}

/** Lets a parent trigger this card's "Expand" action (e.g. the page's Next button). */
export interface ChargerCardHandle {
  expand: () => void;
}

const ChargerCard = forwardRef<ChargerCardHandle, ChargerCardProps>(
  (
    {
      service,
      chargerConfigData,
      allServices,
      serviceIndex,
      showExpandAction = true,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { t } = useTranslation("estimator");
    const format = useFormat();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const { chargerSetup, chargerLocation, wireLength } = chargerConfigData;

    // Distance to this vendor. Prefer the value the backend computed via the
    // active strategy (straight-line or road), falling back to a client-side
    // straight-line calc. null when neither is available, hiding the label.
    // Compute both independently so we can compare them in the logs: the value the
    // BACKEND DistanceProvider returned (haversine or google-road) and the value
    // the FRONTEND computes client-side. The displayed value prefers the backend,
    // falling back to the frontend calc when the backend value is absent.
    const backendDistanceKm: number | null = service?.distanceKm ?? null;
    const frontendDistanceKm = useMemo(
      () =>
        haversineKm(
          chargerConfigData.latitude,
          chargerConfigData.longitude,
          service?.vendor?.latitude,
          service?.vendor?.longitude,
        ),
      [
        chargerConfigData.latitude,
        chargerConfigData.longitude,
        service?.vendor?.latitude,
        service?.vendor?.longitude,
      ],
    );

    const distanceKm = backendDistanceKm ?? frontendDistanceKm;
    const distanceFromBackend = backendDistanceKm != null;

    // Print both side by side so it's clear which is actually being used for the
    // label. Server logs name the backend strategy (haversine vs google-road).
    useEffect(() => {
      const fmt = (value: number | null) =>
        value != null ? `${value.toFixed(2)} km` : "n/a";
      logger.log(
        `ChargerCard distance [${service?.vendor?.businessName ?? "Unknown"}]:`,
        `\n  BACKEND  (DistanceProvider): ${fmt(backendDistanceKm)}`,
        `\n  FRONTEND (client haversine): ${fmt(frontendDistanceKm)}`,
        `\n  DISPLAYED -> ${distanceFromBackend ? "BACKEND" : "FRONTEND"} (${fmt(distanceKm)})`,
      );
    }, [
      backendDistanceKm,
      frontendDistanceKm,
      distanceKm,
      distanceFromBackend,
      service?.vendor?.businessName,
    ]);

    const [selectedAddOn, setSelectedAddOn] = useState({
      id: 0,
      label: "None",
    });
    const [selectedChargerModel, setSelectedChargerModel] =
      useState<ChargerModelOption>(DEFAULT_CHARGER_MODEL);

    const cardStyle: React.CSSProperties = {
      flex: "1 1 320px",
      display: "flex",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      paddingTop: theme.spacing(3),
      boxSizing: "border-box",
      backgroundColor: theme.palette.common.white,
      flexDirection: "column",
      alignItems: "center",
      fontSize: "16px",
      // A hard 280px floor overflowed the narrowest phones once the row
      // gutters were added; the flex-basis still keeps cards wide on desktop.
      minWidth: 0,
      maxWidth: "460px",
    };

    const inclusionsList = useGetFieldByName(
      service?.evChargerServiceFields,
      "inclusions",
    );

    const assumptionsList = useGetFieldByName(
      service?.evChargerServiceFields,
      "assumptions",
    );

    const addOnServicesRaw = useGetFieldByName(
      service?.evChargerServiceFields,
      "addOnServices",
    );

    const chargerModelServicesRaw = useGetFieldByName(
      service?.evChargerServiceFields,
      "evseType",
    );
    const filteredChargerModelServices = chargerModelServicesRaw.filter(
      (service) =>
        isSimilar(`${chargerSetup} (${chargerLocation})`, service.label, 1),
    );

    const addOnServicesList = useMemo(
      () => [{ id: 0, label: "None" }, ...addOnServicesRaw],
      [addOnServicesRaw],
    );

    const chargerModelServicesList = useMemo(
      () => [
        { id: 0, label: "None" },
        ...filteredChargerModelServices.map((s) => ({
          ...s,
          label: s.customLabel || s.label,
        })),
      ],
      [filteredChargerModelServices],
    );

    const isAutoSelect = AUTO_SELECT_CHARGER_SETUPS.includes(chargerSetup);

    const effectiveChargerModel = useMemo(() => {
      if (isAutoSelect || selectedChargerModel.id === 0) {
        const fields = (service?.evChargerServiceFields ??
          []) as ServiceField[];
        return (
          getCheapestChargerModel(fields, chargerSetup, chargerLocation) ??
          DEFAULT_CHARGER_MODEL
        );
      }
      return selectedChargerModel;
    }, [
      isAutoSelect,
      selectedChargerModel,
      service?.evChargerServiceFields,
      chargerSetup,
      chargerLocation,
    ]);

    const { items, total } = usePriceBreakdown(
      service?.evChargerServiceFields,
      {
        wireLength,
        selectedAddOn: selectedAddOn.label,
        selectedChargerModel: effectiveChargerModel.label,
        selectedChargerModelId: effectiveChargerModel.id,
      },
    );

    const navigateToQuote = () => {
      // Carry the chosen service, the full estimator config (used for display and
      // the backend submission), the current selections, and the sibling
      // recommendations (so the quote page can page through them) to the Quote
      // page via router state so it can render and submit real (server-backed)
      // data.
      const quoteNavigationState: QuoteNavigationState = {
        service,
        chargerConfigData,
        selected: {
          chargerModelId: effectiveChargerModel.id,
          addOnId: selectedAddOn.id,
        },
        services: allServices,
        serviceIndex,
      };

      navigate("?currentPage=quote", { state: quoteNavigationState });
    };

    const handleReviewQuote = () => {
      setIsSubmitting(true);
      try {
        navigateToQuote();
      } finally {
        setIsSubmitting(false);
      }
    };

    // Expose Expand so the page's Next button opens this card's quote with the
    // selections currently made on it.
    useImperativeHandle(ref, () => ({ expand: handleReviewQuote }));

    return (
      <div style={cardStyle}>
        {service?.vendor?.logoUrl ? (
          <img
            src={service?.vendor?.logoUrl ?? ""}
            alt={service?.vendor?.businessName ?? t("select.vendor_logo")}
          />
        ) : (
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(1.5rem, 2vw + 1rem, 2.4rem)",
              lineHeight: 1.15,
              textAlign: "center",
            }}
          >
            {service?.vendor?.businessName ?? t("select.unknown_vendor")}
          </h1>
        )}

        <VerifiedBadge verified={service?.vendor?.isVerified} />
        <GoogleRating listing={service?.vendor?.googleListing} />

        {distanceKm != null && (
          <div style={{ opacity: 0.5 }}>
            {t("select.km_away", {
              distance: format.number(distanceKm, { maximumFractionDigits: 1 }),
            })}
          </div>
        )}
        <div style={{ opacity: 0.5 }}>
          {t("select.valid_for", {
            date: format.date(parseInt(service.createdAt)),
          })}
        </div>

        <Typography
          style={{
            fontWeight: "bold",
            margin: 0,
            fontSize: "2rem",
            lineHeight: 1.1,
            textAlign: "center",
          }}
          variant="h2"
        >
          {t("select.total", { amount: format.money(total) })}
        </Typography>

        <Divider
          sx={{
            borderColor: theme.palette.common.black,
            margin: `${theme.spacing(1)} 0`,
            width: "60%",
          }}
        />

        {items.map((item) => {
          return (
            <div
              key={item.id}
              style={{ display: "flex", width: "75%", gap: theme.spacing(2) }}
            >
              <div style={{ flexGrow: 1 }}>{item.label}</div>
              <div>{format.money(item.price)}</div>
            </div>
          );
        })}

        <Divider
          sx={{
            borderColor: theme.palette.common.black,
            margin: `${theme.spacing(1)} 0`,
            width: "60%",
          }}
        />

        <Typography
          style={{ marginBottom: theme.spacing(-1), fontSize: ".93em" }}
          variant="body1"
        >
          {t("select.charger_model")}
        </Typography>

        {!isAutoSelect && (
          <div style={{ width: "95%" }}>
            <AutoComplete
              name="chargerModel"
              textAlign="center"
              value={effectiveChargerModel.id}
              options={chargerModelServicesList.map((option) =>
                option.id === 0
                  ? { ...option, label: t("options.none") }
                  : option,
              )}
              size="tiny"
              onChange={(newValue) => {
                setSelectedChargerModel(
                  (newValue as ChargerModelOption) ?? DEFAULT_CHARGER_MODEL,
                );
              }}
            />
          </div>
        )}

        {inclusionsList.length > 0 && (
          <>
            <Typography
              style={{ marginBottom: theme.spacing(-1), fontSize: "1em" }}
              variant="body1"
            >
              {t("select.inclusions")}
            </Typography>

            <Typography
              style={{ fontSize: ".8em" }}
              variant="body2"
              align="center"
            >
              {inclusionsList.map((item, index) => (
                <span key={item.id}>
                  {item.label}
                  {index < inclusionsList.length - 1 && <br />}
                </span>
              ))}
            </Typography>
          </>
        )}

        {assumptionsList.length > 0 && (
          <>
            <Typography
              style={{ marginBottom: theme.spacing(-1), fontSize: "1em" }}
              variant="body1"
            >
              {t("select.assumptions")}
            </Typography>

            <ul style={{ width: "90%", margin: 0, fontSize: ".8em" }}>
              {assumptionsList.map((item) => (
                <li key={item.id}>{item.label}</li>
              ))}
            </ul>
          </>
        )}

        {service.paymentTerms && (
          <>
            <Typography
              style={{ marginBottom: theme.spacing(-1), fontSize: "1em" }}
              variant="body1"
            >
              {t("select.terms")}
            </Typography>

            <Typography
              style={{ fontSize: ".8em" }}
              variant="body2"
              align="center"
            >
              {service.paymentTerms}
            </Typography>
          </>
        )}

        {addOnServicesList.length > 0 && (
          <>
            <Typography
              style={{ marginBottom: theme.spacing(-1), fontSize: ".93em" }}
              variant="body1"
            >
              {t("select.add_ons")}
            </Typography>

            <div style={{ width: "95%" }}>
              <AutoComplete
                name="addOns"
                value={selectedAddOn.id}
                options={addOnServicesList.map((option) =>
                  option.id === 0
                    ? { ...option, label: t("options.none") }
                    : option,
                )}
                textAlign="center"
                size="tiny"
                onChange={(newVale) => {
                  setSelectedAddOn(newVale ?? { id: 0, label: "None" });
                }}
              />
            </div>
          </>
        )}

        {showExpandAction && (
          <Button
            style={{
              color: theme.palette.common.white,
              fontSize: "1.4em",
              fontWeight: "bolder",
              padding: `2px ${theme.spacing(1)}`,
              width: "95%",
              marginTop: "auto",
              marginBottom: "10px",
            }}
            variant="contained"
            fullWidth
            color="warning"
            onClick={handleReviewQuote}
            size="large"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("select.preparing") : t("select.expand")}
          </Button>
        )}
      </div>
    );
  },
);

ChargerCard.displayName = "ChargerCard";

export default ChargerCard;
